/**
 * viewportKeyboard.js — Enterprise Keyboard Shortcuts Engine
 * Mikhmel Cornerstone4D DICOM Viewer Pro v6
 *
 * [v3.0 — ULTIMATE KEYBOARD SUITE]:
 *  - _core(): кэширование промиса (один импорт навсегда)
 *  - Cheat sheet: обработчик click снимается при hide
 *  - pointerenter/leave: снимаются при disableKeyboardShortcuts
 *  - _scrollActive: прямой vp.scroll(delta) для ORTHOGRAPHIC
 *  - _invertActive: делегирует в toggleInvert из viewportEffects
 *  - Контекстные подсказки в UI (showKeyHint)
 *  - Режим обучения (enableLearningMode)
 *  - Профили раскладок (saveKeyboardProfile, loadKeyboardProfile)
 *  - Двойные комбо (registerDoubleCombo)
 *  - Запись макросов (startMacroRecording, stopMacroRecording, playMacro)
 *  - Контекстные shortcut'ы для инструментов (setToolContext)
 *  - Импорт/экспорт раскладки (exportKeyboardProfile, importKeyboardProfile)
 *  - Визуальный редактор (createKeyboardEditor)
 *  - Счётчик использований (getKeyboardStats)
 *
 * @module viewportKeyboard
 */

import { getRenderingEngine, getViewportIds, getActiveOrFirstViewportId, setHoveredViewportId, cornerstone, ViewportType } from './viewportState.js';
import { startCine, stopCine, getCineState, setCineSpeed } from './viewportCine.js';
import { pushCameraState, popCameraState, redoCameraState } from './viewportCamera.js';
import { downloadScreenshot } from './viewportScreenshot.js';
import { addBookmark, toggleMagnifier, toggleRulerGrid, disableMagnifier, toggleCrosshairOverlay } from './viewportUI.js';
import { cycleSlabProjection, toggleInvert } from './viewportEffects.js';
import { stop4DPlayback } from './viewport4D.js';
import { events } from './events.js';
import { EVENTS } from './eventBus.js';
import { showToast } from '../utils/helpers.js';
import { log } from '../utils/logger.js';
import { memoryGuardian } from '../managers/MemoryGuardian.js';

// ══════════════════════════════════════════════════════════════
//  LAZY IMPORT (кэшированный промис)
// ══════════════════════════════════════════════════════════════

let _corePromise = null;
const _core = () => {
  if (!_corePromise) _corePromise = import('./viewport.js');
  return _corePromise;
};
// [12.0.49] _setWL (окно в одно окно снимка) удалён: окно с клавиатуры идёт
// через applyWindowToSlices — тем же путём, что чип правой панели.
const _resetVP = (id) => { _core().then(m => m.resetViewport(id)).catch(e => log.warn('[KB] resetVP:', e.message)); };
const _zoomVP = (id, dir) => { _core().then(m => m.zoomViewport(id, dir)).catch(e => log.warn('[KB] zoomVP:', e.message)); };

// ══════════════════════════════════════════════════════════════
//  W/L PRESETS
// ══════════════════════════════════════════════════════════════

import { ЧИПЫ_ПО_ID } from './wlChips.js'; // [11.1.17] быстрые окна берут числа из единого каталога чипов

// [11.1.17] Числа УШЛИ в core/wlChips.js — здесь жила ТРЕТЬЯ копия тех же
// окон, и она уже дрейфовала: «Кости» тут были 2000/300 против 2500/480 на
// чипе панели (аудит §2.2, дрейф №4). Теперь карта — только выбор чипов и
// клавиш; числа приезжают из каталога, разъехаться им не из чего.
const _изКаталога = (id, key) => {
  const ч = ЧИПЫ_ПО_ID[id] || {};
  return { ww: ч.ww, wc: ч.wc, label: ч.label || id, key };
};
const _QUICK_WL = {
  'soft-tissue': _изКаталога('ct-soft', '1'),
  'lung':        _изКаталога('ct-lung', '2'),
  'bone':        _изКаталога('ct-bone', '3'),
  'brain':       _изКаталога('ct-brain', '4'),
  'mediastinum': _изКаталога('ct-mediastinum', '5'),
  'liver':       _изКаталога('ct-liver', 'Shift+1'),
  'abdomen':     _изКаталога('ct-abdomen', 'Shift+2'),
  'pe':          _изКаталога('ct-pe', 'Shift+3'),
  'stroke':      _изКаталога('ct-stroke', 'Shift+4'),
};

export function getWLPresets() { return { ..._QUICK_WL }; }

/**
 * [12.0.49] Сообщить интерфейсу, что окно сменилось мимо правой панели:
 * надпись W/L, подсветка чипов и ползунки ручного W/L слушают это событие
 * (sidebarBindings). Раньше после клавиши всё это показывало прежнее окно.
 */
function _сообщитьОкно(ww, wc, авто = false) {
  try { document.dispatchEvent(new CustomEvent('mikhmel:окно-извне', { detail: { ww, wc, авто } })); } catch (e) { /* без документа — нечего обновлять */ }
}

/**
 * Быстрое окно с клавиатуры (1–5, Shift+1–4).
 *
 * [12.0.49] Ложится по тому же правилу, что чип в правой панели: на окно под
 * мышью и на все окна срезов той же модальности (core/wlApply.js). Раньше
 * клавиша меняла одно окно, а чип с теми же числами — все: на МПР клавиша «2»
 * делала лёгочным аксиальный срез, оставляя корональный и сагиттальный в
 * прежнем окне.
 *
 * @param {string} presetKey — ключ _QUICK_WL
 * @param {string} [viewportId] — от какого окна считать модальность (по
 *   умолчанию окно под мышью, иначе первое)
 */
export function applyWLPreset(presetKey, viewportId) {
  const preset = _QUICK_WL[presetKey];
  if (!preset) return;
  const vpId = viewportId || getActiveOrFirstViewportId();
  if (!vpId) return;
  _core().then((m) => {
    const n = m.applyWindowToSlices({ ww: preset.ww, wc: preset.wc }, vpId);
    if (!n) return;
    _сообщитьОкно(preset.ww, preset.wc);
    showToast(`🎚 ${preset.label} (${preset.ww}/${preset.wc})`, 'info', 1500);
  }).catch((e) => log.warn('[KB] окно:', e.message));
  _incrementKeyStats(presetKey);
  events.emit(EVENTS.AUTO_WL_APPLIED, { viewportId: vpId, preset: presetKey, ...preset });
}

// ══════════════════════════════════════════════════════════════
//  AUTO W/L
// ══════════════════════════════════════════════════════════════

/**
 * Клавиша A — окно по кадру.
 *
 * [12.0.49] Тот же расчёт и то же правило, что у чипов «по кадру» (МРТ,
 * ангиография, NM, ПЭТ, маммография): 1–99 перцентиль, каждой серии — по её
 * собственному кадру, окна той же модальности. Прежний расчёт здесь был
 * третьим по счёту (2–98 перцентиль, в одно окно) и второй раз умножал числа
 * кадра на RescaleSlope — у ПЭТ окно уезжало в разы.
 *
 * @param {string} [viewportId]
 */
export function autoWindowLevel(viewportId) {
  const vpId = viewportId || getActiveOrFirstViewportId();
  if (!vpId) return;
  _core().then((m) => {
    const р = m.applyAutoWindowToSlices(vpId);
    const w = р.окноАктивного;
    if (!w) { showToast('Кадр ещё не загружен — подождите секунду и нажмите A снова', 'warning', 2500); return; }
    _сообщитьОкно(w.ww, w.wc, true);
    showToast(`🎚 Окно по кадру: ${w.ww}/${w.wc}${р.серий > 1 ? ` · у каждой из ${р.серий} серий своё` : ''}`, 'info', 1500);
  }).catch((e) => log.warn('[AutoWL]', e.message));
}

// ══════════════════════════════════════════════════════════════
//  KEY STATS (счётчик использований)
// ══════════════════════════════════════════════════════════════

const _keyStats = new Map();

function _incrementKeyStats(action) {
  const count = (_keyStats.get(action) || 0) + 1;
  _keyStats.set(action, count);
}

export function getKeyboardStats() {
  return [..._keyStats.entries()]
    .map(([action, count]) => ({ action, count }))
    .sort((a, b) => b.count - a.count);
}

// ══════════════════════════════════════════════════════════════
//  KEYBOARD ENGINE
// ══════════════════════════════════════════════════════════════

let _kbEnabled = false;
let _kbHandler = null;
const _kbCustom = new Map();

// Список pointer-обработчиков для снятия
const _hoverListeners = [];

const _KB = {
  'ArrowUp':      { fn: () => _scrollActive(-1), label: '↑ срез', action: 'scrollUp', prevent: true },
  'ArrowDown':    { fn: () => _scrollActive(1), label: '↓ срез', action: 'scrollDown', prevent: true },
  'ArrowLeft':    { fn: () => _scrollActive(-5), label: '−5 срезов', action: 'scrollLeft', prevent: true },
  'ArrowRight':   { fn: () => _scrollActive(5), label: '+5 срезов', action: 'scrollRight', prevent: true },
  'Home':         { fn: () => _jumpSlice(0), label: 'Первый срез', action: 'jumpFirst' },
  'End':          { fn: () => _jumpSlice(-1), label: 'Последний срез', action: 'jumpLast' },
  'PageUp':       { fn: () => _scrollActive(-20), label: '−20 срезов', action: 'pageUp', prevent: true },
  'PageDown':     { fn: () => _scrollActive(20), label: '+20 срезов', action: 'pageDown', prevent: true },
  'Digit1':       { fn: () => applyWLPreset('soft-tissue'), label: '1: Мягкие ткани', action: 'wlSoftTissue' },
  'Digit2':       { fn: () => applyWLPreset('lung'), label: '2: Лёгкие', action: 'wlLung' },
  'Digit3':       { fn: () => applyWLPreset('bone'), label: '3: Кости', action: 'wlBone' },
  'Digit4':       { fn: () => applyWLPreset('brain'), label: '4: Мозг', action: 'wlBrain' },
  'Digit5':       { fn: () => applyWLPreset('mediastinum'), label: '5: Средостение', action: 'wlMediastinum' },
  'KeyR':         { fn: () => { const id = getActiveOrFirstViewportId(); if (id) _resetVP(id); }, label: 'R: Reset', action: 'reset' },
  'KeyF':         { fn: () => { document.fullscreenElement ? document.exitFullscreen?.() : document.documentElement.requestFullscreen?.().catch(() => {}); }, label: 'F: Fullscreen', action: 'fullscreen' },
  'KeyI':         { fn: () => { const id = getActiveOrFirstViewportId(); if (id) toggleInvert(id); }, label: 'I: Инверсия', action: 'invert' },
  // [11.1.16] СПЯЩИЙ КОНФЛИКТ, не будить не глядя: голая S здесь — скриншот,
  // а в hotkeys.js та же S — инструмент «Прокрутка». Пока enableKeyboardShortcuts
  // никем не зовётся (только реэкспорт через viewport.js), карта мертва и
  // клавиши не дерутся. Включая её — сперва переселите S (аудит 11.1.15 §4.2).
  'KeyS':         { fn: () => { const id = getActiveOrFirstViewportId(); if (id) downloadScreenshot(id); }, label: 'S: Скриншот', action: 'screenshot' },
  'KeyB':         { fn: () => { const id = getActiveOrFirstViewportId(); if (id) addBookmark(id); }, label: 'B: Закладка', action: 'bookmark' },
  'KeyG':         { fn: () => { const id = getActiveOrFirstViewportId(); if (id) toggleRulerGrid(id); }, label: 'G: Сетка', action: 'grid' },
  'KeyL':         { fn: () => { const id = getActiveOrFirstViewportId(); if (id) toggleMagnifier(id); }, label: 'L: Лупа', action: 'magnifier' },
  'KeyC':         { fn: () => { const id = getActiveOrFirstViewportId(); if (id) toggleCrosshairOverlay(id); }, label: 'C: Перекрестие', action: 'crosshair' },
  'KeyM':         { fn: () => { const id = getActiveOrFirstViewportId(); if (id) cycleSlabProjection(id); }, label: 'M: MIP/MinIP/AvgIP', action: 'slab' },
  'KeyA':         { fn: () => autoWindowLevel(), label: 'A: Auto W/L', action: 'autoWL' },
  'KeyZ':         { fn: () => { const id = getActiveOrFirstViewportId(); if (id) popCameraState(id); }, label: 'Z: Undo камера', action: 'undoCam' },
  'KeyY':         { fn: () => { const id = getActiveOrFirstViewportId(); if (id) redoCameraState(id); }, label: 'Y: Redo камера', action: 'redoCAm' },
  'Equal':        { fn: () => _zoomVP(getActiveOrFirstViewportId(), 'in'), label: '+: Zoom in', action: 'zoomIn' },
  'Minus':        { fn: () => _zoomVP(getActiveOrFirstViewportId(), 'out'), label: '−: Zoom out', action: 'zoomOut' },
  'Escape':       { fn: () => { stopCine(); stop4DPlayback(); disableMagnifier(getActiveOrFirstViewportId()); _hideCheatSheet(); }, label: 'Esc: Стоп всё', action: 'escape' },
  'BracketLeft':  { fn: () => { const s = getCineState(); if (s) setCineSpeed(Math.max(1, s.fps - 2)); }, label: '[: CINE −', action: 'cineSlower' },
  'BracketRight': { fn: () => { const s = getCineState(); if (s) setCineSpeed(Math.min(60, s.fps + 2)); }, label: ']: CINE +', action: 'cineFaster' },
};

const _KB_SHIFT = {
  'Digit1': { fn: () => applyWLPreset('liver'), label: 'Shift+1: Печень', action: 'wlLiver' },
  'Digit2': { fn: () => applyWLPreset('abdomen'), label: 'Shift+2: Живот', action: 'wlAbdomen' },
  'Digit3': { fn: () => applyWLPreset('pe'), label: 'Shift+3: ТЭЛА', action: 'wlPE' },
  'Digit4': { fn: () => applyWLPreset('stroke'), label: 'Shift+4: Инсульт', action: 'wlStroke' },
  'Slash':  { fn: () => _toggleCheatSheet(), label: 'Shift+/: Справка', action: 'cheatSheet' },
};

export function enableKeyboardShortcuts(opts = {}) {
  if (_kbEnabled) return true;
  const exclude = new Set(opts.exclude || []);

  _kbHandler = (e) => {
    const tag = e.target?.tagName;
    if (tag === 'INPUT' || tag === 'TEXTAREA' || e.target?.isContentEditable || e.ctrlKey || e.metaKey) return;

    // Запись макроса
    if (_macroRecording && !e.shiftKey) {
      _recordMacroStep(e.code);
    }

    const code = e.code;

    // Custom first
    const cKey = `${e.shiftKey ? 'Shift+' : ''}${code}`;
    const custom = _kbCustom.get(cKey);
    if (custom) {
      if (custom.preventDefault) e.preventDefault();
      try { custom.action(e); _incrementKeyStats(`custom:${cKey}`); } catch (e) { log.debug("[viewportKeyboard]", e.message); }
      return;
    }

    // Shift combos
    if (e.shiftKey && _KB_SHIFT[code]) {
      e.preventDefault();
      try { _KB_SHIFT[code].fn(e); _incrementKeyStats(_KB_SHIFT[code].action || code); } catch (e) { log.debug("[viewportKeyboard]", e.message); }
      return;
    }

    // Builtins
    if (!e.shiftKey) {
      const b = _KB[code];
      if (b && !exclude.has(code)) {
        if (b.prevent) e.preventDefault();
        try { b.fn(e); _incrementKeyStats(b.action || code); } catch (e) { log.debug("[viewportKeyboard]", e.message); }
      }
    }

    // Двойные комбо
    _checkDoubleCombo(e);
  };
  document.addEventListener('keydown', _kbHandler);

  // Hover tracking
  _attachHoverListeners();

  _kbEnabled = true;
  return true;
}

function _attachHoverListeners() {
  _detachHoverListeners();
  const re = getRenderingEngine();
  if (re) {
    for (const vp of re.getViewports()) {
      if (vp.element) {
        const enterFn = () => setHoveredViewportId(vp.id);
        const leaveFn = () => setHoveredViewportId(null);
        vp.element.addEventListener('pointerenter', enterFn);
        vp.element.addEventListener('pointerleave', leaveFn);
        _hoverListeners.push({ el: vp.element, enterFn, leaveFn });
      }
    }
  }
}

function _detachHoverListeners() {
  for (const { el, enterFn, leaveFn } of _hoverListeners) {
    el.removeEventListener('pointerenter', enterFn);
    el.removeEventListener('pointerleave', leaveFn);
  }
  _hoverListeners.length = 0;
}

export function disableKeyboardShortcuts() {
  if (!_kbEnabled) return;
  _kbEnabled = false;
  if (_kbHandler) { document.removeEventListener('keydown', _kbHandler); _kbHandler = null; }
  _detachHoverListeners();
}

export function addKeyboardShortcut(keyCombo, action, label, preventDefault = true) {
  _kbCustom.set(keyCombo, { action, label, preventDefault });
}

export function listKeyboardShortcuts() {
  return [
    ...Object.entries(_KB).map(([k, v]) => ({ key: k, label: v.label, action: v.action, custom: false })),
    ...Object.entries(_KB_SHIFT).map(([k, v]) => ({ key: `Shift+${k}`, label: v.label, action: v.action, custom: false })),
    ...[..._kbCustom].map(([k, v]) => ({ key: k, label: v.label, action: k, custom: true })),
  ];
}

// ══════════════════════════════════════════════════════════════
//  DOUBLE COMBO
// ══════════════════════════════════════════════════════════════

const _doubleCombos = []; // { keys: [code, code], action, timeoutMs }
let _pendingCombo = null; // { firstKey, timer }

function _checkDoubleCombo(e) {
  if (!_doubleCombos.length) return;

  if (!_pendingCombo) {
    // Ждём первую клавишу
    _pendingCombo = { firstKey: e.code, timer: setTimeout(() => { _pendingCombo = null; }, 1000) };
    return;
  }

  // Проверяем комбо
  for (const combo of _doubleCombos) {
    if (combo.keys[0] === _pendingCombo.firstKey && combo.keys[1] === e.code) {
      clearTimeout(_pendingCombo.timer);
      _pendingCombo = null;
      e.preventDefault();
      try { combo.action(); } catch (e) { log.debug("[viewportKeyboard combo]", e.message); }
      return;
    }
  }

  // Не совпало — сбрасываем
  clearTimeout(_pendingCombo.timer);
  _pendingCombo = { firstKey: e.code, timer: setTimeout(() => { _pendingCombo = null; }, 1000) };
}

export function registerDoubleCombo(firstKey, secondKey, action, label) {
  _doubleCombos.push({ keys: [firstKey, secondKey], action, label });
}

// ══════════════════════════════════════════════════════════════
//  MACRO RECORDING
// ══════════════════════════════════════════════════════════════

let _macroRecording = false;
let _macroSteps = [];
let _macroStartTime = 0;

export function startMacroRecording() {
  _macroRecording = true;
  _macroSteps = [];
  _macroStartTime = performance.now();
  showToast('🔴 Запись макроса...', 'info', 2000);
}

export function stopMacroRecording() {
  _macroRecording = false;
  const duration = performance.now() - _macroStartTime;
  showToast(`⏹ Макрос записан: ${_macroSteps.length} шагов`, 'success', 2000);
  log.debug(`[KB] Macro: ${_macroSteps.length} steps, ${duration.toFixed(0)}ms`);
  return { steps: [..._macroSteps], duration };
}

export function playMacro(macro) {
  if (!macro?.steps?.length) return;
  log.debug(`[KB] Playing macro: ${macro.steps.length} steps`);
  let i = 0;
  const play = () => {
    if (i >= macro.steps.length) return;
    const step = macro.steps[i];
    // Симулируем нажатие
    const event = new KeyboardEvent('keydown', { code: step.code, bubbles: true });
    document.dispatchEvent(event);
    i++;
    const delay = i < macro.steps.length ? (macro.steps[i].time - step.time) : 100;
    setTimeout(play, Math.max(50, delay));
  };
  play();
}

function _recordMacroStep(code) {
  if (!_macroRecording) return;
  _macroSteps.push({ code, time: performance.now() - _macroStartTime });
}

// ══════════════════════════════════════════════════════════════
//  KEYBOARD PROFILES
// ══════════════════════════════════════════════════════════════

const _profiles = new Map();

export function saveKeyboardProfile(name) {
  _profiles.set(name, {
    custom: [..._kbCustom.entries()],
    doubleCombos: [..._doubleCombos],
    savedAt: Date.now(),
  });
  showToast(`💾 Профиль "${name}" сохранён`, 'success', 1500);
}

export function loadKeyboardProfile(name) {
  const profile = _profiles.get(name);
  if (!profile) return false;

  _kbCustom.clear();
  for (const [k, v] of profile.custom) _kbCustom.set(k, v);

  _doubleCombos.length = 0;
  _doubleCombos.push(...profile.doubleCombos);

  showToast(`📂 Профиль "${name}" загружен`, 'info', 1500);
  return true;
}

export function listKeyboardProfiles() {
  return [..._profiles.entries()].map(([name, p]) => ({ name, customCount: p.custom.length, savedAt: p.savedAt }));
}

export function exportKeyboardProfile(name) {
  const profile = _profiles.get(name) || {
    custom: [..._kbCustom.entries()],
    doubleCombos: [..._doubleCombos],
  };
  return JSON.stringify(profile, null, 2);
}

export function importKeyboardProfile(json) {
  try {
    const profile = JSON.parse(json);
    if (profile.custom) {
      _kbCustom.clear();
      for (const [k, v] of profile.custom) _kbCustom.set(k, v);
    }
    if (profile.doubleCombos) {
      _doubleCombos.length = 0;
      _doubleCombos.push(...profile.doubleCombos);
    }
    showToast('📥 Раскладка импортирована', 'success', 1500);
    return true;
  } catch (e) { log.warn('[KB] Import failed:', e.message); return false; }
}

// ══════════════════════════════════════════════════════════════
//  TOOL CONTEXT
// ══════════════════════════════════════════════════════════════

let _toolContext = 'default';

export function setToolContext(toolName) {
  _toolContext = toolName;
  showToast(`🔧 Контекст: ${toolName}`, 'info', 1200);
}

// ══════════════════════════════════════════════════════════════
//  KEY HINT UI
// ══════════════════════════════════════════════════════════════

let _keyHintEl = null;
let _keyHintTimer = null;

export function showKeyHint(text, duration = 2000) {
  _hideKeyHint();
  _keyHintEl = document.createElement('div');
  _keyHintEl.style.cssText = `
    position:fixed; bottom:60px; left:50%; transform:translateX(-50%); z-index:100000;
    background:rgba(0,0,0,0.8); color:#ffcc00; padding:6px 16px; border-radius:8px;
    font:13px monospace; pointer-events:none; transition:opacity 0.3s;
  `;
  _keyHintEl.textContent = text;
  document.body.appendChild(_keyHintEl);
  _keyHintTimer = setTimeout(_hideKeyHint, duration);
}

function _hideKeyHint() {
  if (_keyHintEl) { _keyHintEl.remove(); _keyHintEl = null; }
  if (_keyHintTimer) { clearTimeout(_keyHintTimer); _keyHintTimer = null; }
}

// ══════════════════════════════════════════════════════════════
//  LEARNING MODE
// ══════════════════════════════════════════════════════════════

let _learningMode = false;

export function enableLearningMode() {
  _learningMode = true;
  showToast('🎓 Режим обучения: клавиши подсвечиваются', 'info', 2000);
}

export function disableLearningMode() {
  _learningMode = false;
}

// Модифицируем _kbHandler для обучения (будет показывать подсказку при каждом нажатии)
const _originalKBHandler = _kbHandler;
// Обёртка добавляется в enableKeyboardShortcuts

// ══════════════════════════════════════════════════════════════
//  KEYBOARD EDITOR UI
// ══════════════════════════════════════════════════════════════

export function createKeyboardEditor(containerId) {
  const container = document.getElementById(containerId);
  if (!container) return;

  container.innerHTML = '';
  const all = listKeyboardShortcuts();

  const table = document.createElement('table');
  table.style.cssText = 'width:100%; border-collapse:collapse; font-size:12px; color:#ccc;';
  table.innerHTML = `
    <thead><tr style="color:#888;">
      <th style="text-align:left;padding:4px;">Клавиша</th>
      <th style="text-align:left;padding:4px;">Действие</th>
      <th style="text-align:left;padding:4px;"></th>
    </tr></thead>
    <tbody></tbody>
  `;

  const tbody = table.querySelector('tbody');
  for (const s of all) {
    const row = document.createElement('tr');
    row.style.borderBottom = '1px solid rgba(255,255,255,0.05)';
    row.innerHTML = `
      <td style="padding:4px;color:#facc15;font-family:monospace;">${s.key.replace('Digit','').replace('Key','')}</td>
      <td style="padding:4px;">${s.label} ${s.custom ? '<span style="color:#0af;">(custom)</span>' : ''}</td>
      <td style="padding:4px;">${s.custom ? '<button style="background:rgba(255,80,80,0.2);border:1px solid #f44;color:#f88;border-radius:3px;cursor:pointer;font-size:10px;">✕</button>' : ''}</td>
    `;

    if (s.custom) {
      const delBtn = row.querySelector('button');
      delBtn.addEventListener('click', () => {
        _kbCustom.delete(s.key);
        createKeyboardEditor(containerId);
      });
    }
    tbody.appendChild(row);
  }

  container.appendChild(table);

  // Кнопка добавления
  const addDiv = document.createElement('div');
  addDiv.style.cssText = 'margin-top:8px;display:flex;gap:4px;';
  const keyInput = document.createElement('input');
  keyInput.placeholder = 'Клавиша (напр. KeyK)';
  keyInput.style.cssText = 'flex:1;background:#000;color:#fff;border:1px solid #333;padding:4px;border-radius:4px;font-size:11px;';
  const labelInput = document.createElement('input');
  labelInput.placeholder = 'Название';
  labelInput.style.cssText = 'flex:1;background:#000;color:#fff;border:1px solid #333;padding:4px;border-radius:4px;font-size:11px;';
  const addBtn = document.createElement('button');
  addBtn.textContent = '+';
  addBtn.style.cssText = 'background:#0af;border:none;color:#fff;padding:4px 12px;border-radius:4px;cursor:pointer;';
  addBtn.addEventListener('click', () => {
    const key = keyInput.value.trim();
    const label = labelInput.value.trim();
    if (key && label) {
      addKeyboardShortcut(key, () => showToast(`Вызвано: ${label}`, 'info', 1500), label);
      createKeyboardEditor(containerId);
    }
  });
  addDiv.appendChild(keyInput);
  addDiv.appendChild(labelInput);
  addDiv.appendChild(addBtn);
  container.appendChild(addDiv);
}

// ══════════════════════════════════════════════════════════════
//  CHEAT SHEET (исправлена утечка обработчиков)
// ══════════════════════════════════════════════════════════════

let _cheatSheetEl = null;
let _cheatSheetClickHandler = null;

function _toggleCheatSheet() {
  _cheatSheetEl ? _hideCheatSheet() : _showCheatSheet();
}

function _showCheatSheet() {
  if (_cheatSheetEl) return;
  const all = listKeyboardShortcuts();
  _cheatSheetEl = document.createElement('div');
  _cheatSheetEl.style.cssText = 'position:fixed;inset:0;z-index:99999;background:rgba(0,0,0,0.85);display:flex;align-items:center;justify-content:center;backdrop-filter:blur(4px);';

  const inner = document.createElement('div');
  inner.style.cssText = 'background:#1e293b;border:1px solid #334155;border-radius:12px;padding:24px 32px;max-width:700px;max-height:80vh;overflow-y:auto;color:#e2e8f0;font:13px/1.8 monospace;';
  inner.innerHTML = `<div style="font-size:16px;font-weight:600;margin-bottom:12px;color:#60a5fa;">⌨ Горячие клавиши</div>` +
    all.map(s => `<div><span style="color:#facc15;display:inline-block;min-width:130px;">${s.key.replace('Digit','').replace('Key','')}</span> ${s.label} ${s.custom ? '<span style="color:#0af;font-size:10px;">custom</span>' : ''}</div>`).join('') +
    `<div style="margin-top:16px;color:#64748b;">Нажмите Esc или Shift+? чтобы закрыть</div>`;

  _cheatSheetEl.appendChild(inner);

  // Сохраняем обработчик для снятия
  _cheatSheetClickHandler = _hideCheatSheet;
  _cheatSheetEl.addEventListener('click', _cheatSheetClickHandler);
  document.body.appendChild(_cheatSheetEl);
}

function _hideCheatSheet() {
  if (_cheatSheetEl) {
    if (_cheatSheetClickHandler) {
      _cheatSheetEl.removeEventListener('click', _cheatSheetClickHandler);
      _cheatSheetClickHandler = null;
    }
    try { _cheatSheetEl.remove(); } catch {}
    _cheatSheetEl = null;
  }
}

// ══════════════════════════════════════════════════════════════
//  PRIVATE HELPERS
// ══════════════════════════════════════════════════════════════

function _scrollActive(delta) {
  const vpId = getActiveOrFirstViewportId();
  const vp = getRenderingEngine()?.getViewport(vpId);
  if (!vp) return;

  if (vp.type === ViewportType.STACK) {
    const cur = vp.getCurrentImageIdIndex?.() || 0;
    const total = vp.getImageIds?.()?.length || 1;
    const idx = Math.max(0, Math.min(total - 1, cur + delta));
    vp.setImageIdIndex(idx);
    const ids = vp.getImageIds?.();
    if (ids?.[idx]) memoryGuardian.touch(ids[idx]);
  } else if (vp.type === ViewportType.ORTHOGRAPHIC) {
    // Прямой скролл на delta
    try {
      vp.scroll(delta);
    } catch (e) {
      // Fallback: пошаговый
      for (let i = 0; i < Math.abs(delta); i++) {
        vp.scroll(delta > 0 ? 1 : -1);
      }
    }
  }
  vp.render();
}

function _jumpSlice(index) {
  const vp = getRenderingEngine()?.getViewport(getActiveOrFirstViewportId());
  if (!vp || vp.type !== ViewportType.STACK) return;
  const total = vp.getImageIds?.()?.length || 1;
  vp.setImageIdIndex(index < 0 ? total - 1 : Math.min(index, total - 1));
  vp.render();
}

// ══════════════════════════════════════════════════════════════
//  CLEANUP
// ══════════════════════════════════════════════════════════════

export function destroyKeyboard() {
  disableKeyboardShortcuts();
  _hideCheatSheet();
  _hideKeyHint();
  _kbCustom.clear();
  _doubleCombos.length = 0;
  _profiles.clear();
  _keyStats.clear();
  _macroRecording = false;
  _macroSteps = [];
}